import React from "react"
import Icon from "@kit/design/icon"
import { useEditingContext, useElement } from "../../data/form-data"
import { ContentEditable } from "../content-editable"
import { useBuilderState } from "app/forms/builder/builder-context"
import Masonry from "@kit/design/masonry"

const ASPECT_RATIOS = {
  landscape: "56.25%", // 16:9 aspect ratio; 6 ÷ 9
  portrait: "133%", // 3:4 aspect ratio; 4 ÷ 3
  square: "100%" // 1:1 aspect ratio; 1 ÷ 1
}

const generateCSS = option => {
  if (option === "none") return null
  return { paddingBottom: ASPECT_RATIOS[option], height: 0 }
}

const COLUMN_CLASSES = {
  1: "gap-8 grid-cols-1",
  2: "gap-8 grid-cols-2",
  3: "gap-4 grid-cols-3",
  4: "gap-2 grid-cols-4"
}

function GridImage({ element_id, src, alt, settings: { radius, crop } }) {
  const { setActiveElement } = useBuilderState()

  const styles = {
    borderRadius: radius
  }
  const classes = `filter brighten hover:brighten h-full w-full ${
    crop === "none" ? "" : "absolute inset-0 object-cover object-center"
  }`

  const handleClick = () => setActiveElement(element_id)

  return (
    <figure>
      <button
        onClick={handleClick}
        className="block relative w-full cursor-pointer focus:outline-none focus:gallery"
        style={generateCSS(crop)}
      >
        <img src={src} alt={alt} style={styles} className={classes} />
      </button>
      <Caption element_id={element_id} />
    </figure>
  )
}

export function Caption({ element_id }) {
  const [element, updateElement] = useElement(element_id)
  const { caption } = element
  const editing = useEditingContext()

  const handleChange = ({ content }) =>
    updateElement({ name: element_id, caption: content })

  if (editing) {
    return (
      <ContentEditable
        tag="figcaption"
        className="mt-2 caption-placeholder text-center"
        content={caption}
        onChange={handleChange}
        editing={editing}
      />
    )
  } else if (caption) {
    return <figcaption className="mt-2">{caption}</figcaption>
  } else {
    return null
  }
}

function GridUploader({ settings: { radius, crop }, addImage }) {
  const buttonStyles = {
    borderRadius: radius
  }

  const iconStyles = {
    width: 20,
    height: 20
  }

  return (
    <div
      className={`relative w-full ${crop === "none" ? "h-40" : ""}`}
      style={generateCSS(crop)}
    >
      <button
        className={`border-2 flex items-center justify-center w-full h-full text-gray-500 border-dashed flex-col hover:text-gray-700 transition duration-200 border-gray-500 hover:border-gray-700 ${
          crop === "none" ? "" : "absolute inset-0"
        }`}
        style={buttonStyles}
        onClick={addImage}
      >
        <Icon name="plus-circle" style={iconStyles} />
        <div className="mt-3 font-semibold text-base leading-none font-sans">
          Add image
        </div>
      </button>
    </div>
  )
}

export function GridGallery({ images, settings, addImage, style }) {
  const editing = useEditingContext()
  const { columns, crop } = settings

  return crop === "none" ? (
    <Masonry columns={columns} gap={4}>
      {images.map(image => (
        <GridImage key={image.element_id} {...image} settings={settings} />
      ))}
      {editing && <GridUploader settings={settings} addImage={addImage} />}
    </Masonry>
  ) : (
    <div className={`grid ${COLUMN_CLASSES[columns]}`} style={style}>
      {images.map(image => (
        <GridImage key={image.element_id} {...image} settings={settings} />
      ))}
      {editing && <GridUploader settings={settings} addImage={addImage} />}
    </div>
  )
}
